import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { CalculationOutput } from "components/ui/display/CalculationOutput"
import { Input } from "components/ui/inputs/Input"
import { useErrors } from "providers/ErrorsProvider"
import { useEffect, useState } from "react"
import { calculationLimits, signalDurations } from "store/calculation.config"
import styled from "styled-components"
import { calculateSignal } from "./Signal.calculate"

export const Signal = () => {

    const {setError, unsetError} = useErrors();
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        regulatedLength: '',
        trafficForward: '',
        trafficBackward: ''
    });

    const [calculationInput, setCalculationInput] = useState({
        speedlimit: '',
        regulatedLength: '',
        trafficForward: '',
        trafficBackward: ''
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateSignal(calculationInput);
        //console.log(context);
        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }
        
        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });
        
    }, [calculationInput, setError, unsetError]);

    return (
        <StyledSignal>
            <ToolsColumn className='inputColumn'>
                <ToolsContainer title='Inndata'>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Regulert strekning"
                        prefix={<span>L</span>}
                        sufix='m'
                        type='number'
                        error={calculatedState.input?.regulatedLength?.severity === 'error' ?? null}
                        warning={calculatedState.input?.regulatedLength?.severity === 'warning' ?? null}
                        value={rawInput.regulatedLength}
                        onChange={(e) => setRawInput(prev => ({...prev, regulatedLength: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, regulatedLength: e.target.value}))}
                    />
                    <Input
                        title="Timetrafikk 1. trafikkstrøm"
                        prefix={<span>Q<sub>1</sub></span>}
                        sufix='kjt/t'
                        type='number'
                        error={calculatedState.input?.trafficForward?.severity === 'error' ?? null}
                        warning={calculatedState.input?.trafficForward?.severity === 'warning' ?? null}
                        value={rawInput.trafficForward}
                        onChange={(e) => setRawInput(prev => ({...prev, trafficForward: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, trafficForward: e.target.value}))}
                    />
                    <Input
                        title="Timetrafikk 2. trafikkstrøm"
                        prefix={<span>Q<sub>2</sub></span>}
                        sufix='kjt/t'
                        type='number'
                        error={calculatedState.input?.trafficBackward?.severity === 'error' ?? null}
                        warning={calculatedState.input?.trafficBackward?.severity === 'warning' ?? null}
                        value={rawInput.trafficBackward}
                        onChange={(e) => setRawInput(prev => ({...prev, trafficBackward: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, trafficBackward: e.target.value}))}
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                <CalculationOutput
                    title="Tømmetid"
                    prefix={<span>T<sub>1</sub></span>}
                    value={calculatedState.output?.emptyingTime?.value?.toFixed(1) ?? '--'}
                    error={calculatedState.output?.emptyingTime?.severity === 'error'}
                    sufix='s'
                />
                <CalculationOutput
                    title="Grønntid, 1. trafikkstrøm"
                    prefix={<span>T<sub>2,1</sub></span>}
                    value={calculatedState.output?.greenTimeForward?.value?.toFixed(1) ?? '--'}
                    error={calculatedState.output?.greenTimeForward?.severity === 'error'}
                    warning={calculatedState.output?.greenTimeForward?.severity === 'warning'}
                    sufix='s'
                />
                <CalculationOutput
                    title="Grønntid, 2. trafikkstrøm"
                    prefix={<span>T<sub>2,2</sub></span>}
                    value={calculatedState.output?.greenTimeBackward?.value?.toFixed(1) ?? '--'}
                    error={calculatedState.output?.greenTimeBackward?.severity === 'error'}
                    warning={calculatedState.output?.greenTimeBackward?.severity === 'warning'}
                    sufix='s'
                />
                <CalculationOutput
                    title="Omløpstid"
                    prefix={<span>T<sub>3</sub></span>}
                    value={calculatedState.output?.correctedCycleTime?.value?.toFixed(1) ?? '--'}
                    error={calculatedState.output?.correctedCycleTime?.severity === 'error'}
                    warning={calculatedState.output?.correctedCycleTime?.severity === 'warning'}
                    sufix='s'
                />
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                <SignalDurationTable
                    traffic={calculatedState.derived?.trafficTotal?.value ?? null}
                    emptyingTime={calculatedState.output?.emptyingTime?.value ?? null}
                />
                {
                    calculatedState.output?.signalDesignForward?.valid || calculatedState.output?.signalDesignBackward?.valid ?
                    <ToolsContainer title='Utforming av signalregulering'>
                        { calculatedState.output?.signalDesignForward?.valid ? <SignalDesignBar name='1. Trafikkstrøm' signalDesign={calculatedState.output.signalDesignForward.value}/> : null}
                        { calculatedState.output?.signalDesignBackward?.valid ? <SignalDesignBar name='2. Trafikkstrøm' signalDesign={calculatedState.output.signalDesignBackward.value}/> : null}
                    </ToolsContainer>
                    : null
                }
            </ToolsColumn>
        </StyledSignal>
    )
}

const StyledSignal = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }
`

const SignalDurationTable = ({traffic = null, emptyingTime = null}) => {
    let hydrated = false;

    return (
        <StyledSignalDurationTable>
            <table>
                <colgroup>
                    <col style={{ minWidth: 70 }} />
                    <col style={{ minWidth: 70 }} />
                    <col style={{ minWidth: 70 }} />
                    <col style={{ minWidth: 70 }} />
                    <col style={{ minWidth: 70 }} />
                    <col style={{ minWidth: 70 }} />
                    <col style={{ minWidth: 70 }} />
                </colgroup>
                <thead>
                    <tr>
                        <th>Tømmetid</th>
                        <th>6 s.</th>
                        <th>10 s.</th>
                        <th>20 s.</th>
                        <th>30 s.</th>
                        <th>40 s.</th>
                        <th>50 s.</th>
                    </tr>
                    <tr>
                        <th>Trafikk</th>
                        <th colSpan={6}>Grønntid / omløpstid</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        signalDurations.map((el, i) => 
                            <tr key={i}>
                                <td>{el.traffic}</td>
                                {
                                    el.values.map(ev =>
                                        {
                                            let act = false;
                                            if(traffic && emptyingTime && el.traffic >= traffic && ev.emptyingTime >= emptyingTime && !hydrated)
                                            {
                                                act = true;
                                                hydrated = true;
                                            }
                            
                                            return (
                                                <td
                                                    className={act ? 'isActive' : null}
                                                    key={ev.greenTime+'-'+ev.cycleTime}
                                                >
                                                    {ev.greenTime + '/' + ev.cycleTime}
                                                </td>
                                            )
                                        }
                                        
                                    )
                                }
                            </tr>
                        )
                    }
                </tbody>
            </table>
        </StyledSignalDurationTable>
    )
}

const StyledSignalDurationTable = styled.div`
    overflow: auto;

    table
    {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;

        thead
        {
            tr th
            {
                padding: ${({theme}) => theme.spacing.sm};
                background: ${({theme}) => theme.colors.accentBackground};
            }

            tr:first-child
            {
                th
                {
                    border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryForeground};
                }

                th:first-child
                {
                    border-top-left-radius: ${({theme}) => theme.cornerRadius.md};
                }

                th:last-child
                {
                    border-top-right-radius: ${({theme}) => theme.cornerRadius.md};
                }
            }

            tr:last-child
            {
                th:first-child
                {
                    border-right: 1px solid ${({theme}) => theme.colors.neutralPrimaryForeground};
                }
            }
        }

        tbody
        {
            tr
            {
                td
                {
                    padding: ${({theme}) => theme.spacing.sm};
                    text-align: center;
                    transition-duration: 220ms;

                    &.isActive
                    {
                        background: ${({theme}) => theme.colors.accentForeground};
                        color: ${({theme}) => theme.colors.neutralPrimaryBackground};
                        font-weight: 600;
                        border-radius: ${({theme}) => theme.cornerRadius.md};
                    }
                }

                td:first-child
                {
                    border-right: 1px solid ${({theme}) => theme.colors.neutralPrimaryForeground};
                }
            }
        }
    }
`

const SignalDesignBar = ({name, signalDesign}) => {

    return (
        <StyledSignalDesignBar>
            <div className="signalDesignTitle">{name}</div>
            <div className="signalDesignContent">
                {
                    signalDesign.map((el, i) => 
                        <div
                            key={i}
                            style={{width: 'calc(100*' + el.percentage + '%)'}}
                            className={el.type === 'green' ? 'greenSignal' : el.type === 'yellow' ? 'yellowSignal' : el.type === 'red' ? 'redSignal' : el.type === 'emptying' ? 'emptyingSignal' : null}
                        >
                            <span>{el.duration.toFixed(1)} s.</span>
                        </div>
                    )
                }
            </div>
        </StyledSignalDesignBar>
    )
}

const StyledSignalDesignBar = styled.div`

    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacing.md};
    margin-bottom: ${({theme}) => theme.spacing.xl};

    div.signalDesignTitle
    {
        font-weight: 600;
        font-size: ${({theme}) => theme.fontSize.sm};
    }

    div.signalDesignContent
    {
        height: ${({theme}) => theme.fontSize.xxl};
        display: flex;

        > div
        {
            display: flex;
            align-items: center;
            justify-content: center;
            
            position: relative;
            transition-duration: 220ms;

            &:first-child
            {
                border-top-left-radius: ${({theme}) => theme.cornerRadius.md};
                border-bottom-left-radius: ${({theme}) => theme.cornerRadius.md};
            }

            &:last-child
            {
                border-top-right-radius: ${({theme}) => theme.cornerRadius.md};
                border-bottom-right-radius: ${({theme}) => theme.cornerRadius.md};
            }

            &.greenSignal
            {
                background: rgba(45, 201, 55, 0.5);
                border: 1px solid #2dc937;
            }

            &.yellowSignal
            {
                background: rgba(231, 180, 22, 0.5);
                border: 1px solid #e7b416;
            }

            &.redSignal
            {
                background: rgba(204, 50, 50, 0.75);
                border: 1px solid #cc3232;
            }

            &.emptyingSignal
            {
                border: 1px solid #cc3232;
                background:
                    repeating-linear-gradient(
                        45deg,
                        rgba(204, 50, 50, 0.75) 0 4px,
                        transparent 4px 8px
                    );
            }

            > span
            {
                white-space: nowrap;
                position: absolute;
                font-size: ${({theme}) => theme.fontSize.sm};
                font-weight: 600;
                top: calc(100% + ${({theme}) => theme.spacing.md});
                //bottom: ;
                left: 50%;
                transform: translateX(-50%);
            }
        }
    }
`